import { FormSelect, P } from '@fashionnovateam/nova-ui-toolkit';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ControlledFormInput } from '../../../../Lib/Utils/FormUtils/ControlledFormInput';
import CountryList from '../../../../Lib/Constants/countries';
import {
  CounterQuoteStatus,
  counterQuoteStatusDisplayMapping,
  CounterQuoteStatusToHide,
} from '../../types';
import { useCounterContext } from '../../hooks/useCounter';

const countrySelectItems = [
  ...CountryList.map((country) => ({
    value: country.value,
    label: country.text,
  })),
];

function QuantityForm() {
  const { control } = useFormContext();
  const { selectedCounter } = useCounterContext();
  const quoteStatusToHide = Object.values(CounterQuoteStatusToHide) as string[];

  const quoteStatus = quoteStatusToHide.includes(
    selectedCounter?.counter_quote_status as string,
  )
    ? selectedCounter?.prev_counter_quote_status
    : (selectedCounter?.counter_quote_status as CounterQuoteStatus);
  const mappedStatus =
    counterQuoteStatusDisplayMapping[
      quoteStatus as keyof typeof counterQuoteStatusDisplayMapping
    ];
  const formEnabled = [
    CounterQuoteStatus.NOT_SENT,
    CounterQuoteStatus.SENT,
  ].includes(mappedStatus as CounterQuoteStatus);

  const validationRules = (fieldName: string) => ({
    valueAsNumber: true,
    min: {
      value: 0,
      message: `${fieldName} must be greater than or equal to 0`,
    },
  });

  return (
    <div className="border rounded-xl border-neutral-200 p-5">
      <div className="flex gap-4 mt-4">
        <div className="flex-1">
          <ControlledFormInput
            control={control}
            name="moq"
            label="MOQ"
            inputType="number"
            required
            rules={validationRules('MOQ')}
            disabled={!formEnabled}
            inputProps={{ min: '0' }}
          />
        </div>
        <div className="flex-1">
          <ControlledFormInput
            control={control}
            name="mcq"
            label="MCQ"
            inputType="number"
            required
            rules={validationRules('MCQ')}
            disabled={!formEnabled}
            inputProps={{ min: '0' }}
          />
        </div>
      </div>
      <div className="flex gap-4 mt-4">
        <div className="flex-1">
          <ControlledFormInput
            control={control}
            name="lead_time"
            label="Lead Time"
            inputType="number"
            required
            rules={validationRules('Lead Time')}
            disabled={!formEnabled}
            inputProps={{
              min: '0',
              endAdornment: (
                <P size="xs" color="disabled">
                  weeks
                </P>
              ),
            }}
          />
        </div>
        <div className="flex-1">
          <ControlledFormInput
            control={control}
            name="reorder_lead_time"
            label="Reorder Lead Time"
            inputType="number"
            required
            disabled={!formEnabled}
            inputProps={{
              min: '0',
              endAdornment: (
                <P size="xs" color="disabled">
                  weeks
                </P>
              ),
            }}
            rules={validationRules('Reorder Lead Time')}
          />
        </div>
      </div>
      <div className="flex gap-4 mt-4">
        <div className="w-1/2">
          <Controller
            control={control}
            name="coo"
            render={({ field, fieldState: { error } }) => (
              <FormSelect
                label="COO"
                inputId="coo"
                options={[{ label: '', value: '' }, ...countrySelectItems]}
                required
                value={field?.value || ''}
                disabled={!formEnabled}
                error={error?.message}
                onChangeValue={field.onChange}
                className="[&_select]:nu-text-base-secondary"
              />
            )}
          />
        </div>
        <div className="w-1/2">
          <Controller
            control={control}
            name="shipment_mode"
            render={({ field, fieldState: { error } }) => (
              <FormSelect
                label="Shipment Mode"
                inputId="shipment_mode"
                options={[
                  { label: '', value: '' },
                  { value: 'air', label: 'Air' },
                  { value: 'land', label: 'Land' },
                  { value: 'sea', label: 'Sea' },
                ]}
                required
                value={field?.value || ''}
                disabled={!formEnabled}
                error={error?.message}
                onChangeValue={field.onChange}
                className="[&_select]:nu-text-base-secondary"
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}

export default QuantityForm;
